import React from "react";
import FileSaver from "file-saver";
import { message, Modal, ModalFuncProps } from "antd";
import moment from "moment";
import ShopType from "@/enum/ShopType";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import LogisticsCompany from "@/enum/LogisticsCompany";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import { getSETokenFromQn } from "@qn/services/api/getToken";
import { FD_BASE_URL } from "@/config";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import OrderStatus from "@/enum/OrderStatus";

// uuid生成
function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;

        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// 生成 Antd 的Form表单布局
function getFormLayout(span = 4) {
    const spanCount = 24;
    const wrapperSpan = spanCount - span;
    const formItemLayout = {
        labelCol: {
            xs: { span },
            sm: { span },
        },
        wrapperCol: {
            xs: { span: wrapperSpan },
            sm: { span: wrapperSpan },
        },
    };

    const formButtonLayout = {
        wrapperCol: {
            xs: { span: wrapperSpan, offset: span },
            sm: { span: wrapperSpan, offset: span },
        },
    };

    return [formItemLayout, formButtonLayout];
}

// 判断是否虚拟打印机
function isVirtualPrinter(printerName: any) {
    return (
        printerName === "Microsoft XPS Document Writer" ||
        printerName === "Microsoft Office Document Image Writer" ||
        printerName.indexOf("OneNote") > -1 ||
        printerName.indexOf("PDF") > -1
    );
}

/**
 * 虚拟打印机排在后面
 */
function filterPrinters(printers: any) {
    if (!printers) {
        return [];
    }
    const realPrinters: any = [];
    const virtualPrinters: any = [];
    printers.map((printer: any) => {
        const printerName = printer.name;

        if (printerName === "Fax") {
            return;
        }

        if (isVirtualPrinter(printerName)) {
            virtualPrinters.push({
                value: printerName,
                label: `${printerName}（虚拟打印机）`,
                isVirtual: true,
            });
        } else {
            realPrinters.push({
                value: printerName,
                label: printerName,
            });
        }
    });

    return [...realPrinters, ...virtualPrinters];
}

function numberTransformer(value: any, positive = true) {
    const number = parseFloat(value);
    if (Number.isNaN(number)) return 0;
    if (positive) {
        if (number < 0) return 0;
    }
    return number;
}

async function isNonnegativeValidator(rule: any, value: any) {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(value)) return Promise.reject(new Error("请输入一个有效的数字"));
    if (value < 0) return Promise.reject(new Error("请输入一个大于等于0的数字"));

    return Promise.resolve();
}

function confirm(title: React.ReactNode, content?: React.ReactNode) {
    return new Promise<boolean>((resolve, reject) => {
        Modal.confirm({
            title,
            content,
            onOk() {
                resolve(true);
            },
            onCancel() {
                reject("用户取消操作");
            },
            focusable: { focusTriggerAfterClose: true },
        });
    });
}

export function aliCanNotShipOrderConfirm() {
    return new Promise<boolean>((resolve, reject) => {
        Modal.confirm({
            title: "所选订单包含阿里不能发货订单",
            onOk() {
                resolve(true);
            },
            onCancel() {
                reject(new Error("用户取消操作"));
            },
            focusable: { focusTriggerAfterClose: true },
            content: <span className="error">请先查看收货人地址下方的不可发货原因并确认无误之后再操作</span>,
        });
    });
}

export function tbCanNotShipOrderConfirm() {
    return new Promise<boolean>((resolve, reject) => {
        Modal.confirm({
            title: "所选订单包含淘宝不能发货订单",
            onOk() {
                resolve(true);
            },
            onCancel() {
                reject(new Error("用户取消操作"));
            },
            focusable: { focusTriggerAfterClose: true },
            content: <span className="error">请先确认无误之后再操作</span>,
        });
    });
}

export function canNotShipOrderConfirm() {
    return new Promise<boolean>((resolve, reject) => {
        Modal.confirm({
            title: "所选订单包含不可发货订单",
            onOk() {
                resolve(true);
            },
            onCancel() {
                reject(new Error("用户取消操作"));
            },
            focusable: { focusTriggerAfterClose: true },
            content: <span className="error">请先查看收货人地址下方的不可发货原因并确认无误之后再操作</span>,
        });
    });
}

export function doudianOrderSupplierPrintConfirm() {
    return new Promise<boolean>((resolve, reject) => {
        Modal.confirm({
            title: "所选订单是供应链订单，订单内有商品由供应链商家发货，继续打印会有资金损失风险，是否继续打印？",
            onOk() {
                resolve(true);
            },
            onCancel() {
                reject(new Error("用户取消操作"));
            },
            focusable: { focusTriggerAfterClose: true },
        });
    });
}

function timeFormat(time?: string | number, format: string = "YYYY-MM-DD HH:mm") {
    if (!time) {
        return "";
    }

    return moment(time).format(format);
}

export function getByteLength(val: string = "") {
    var len = 0;
    for (var i = 0; i < val.length; i++) {
        var a = val.charAt(i);
        if (a.match(/[^\x00-\xff]/gi) != null) {
            len += 2;
        } else {
            len += 1;
        }
    }
    return len;
}

function isSameArrayValue(arr1, arr2) {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((v) => arr2.some((k) => v == k));
}

/**过滤指定店铺类型 */
function filterSpecificShopType(shops: ShopDTO[], types: ShopType[]) {
    const newShops: ShopDTO[] = [];

    shops.forEach((shop) => {
        if (!types.includes(shop.type)) {
            newShops.push(shop);
        }
    });

    return newShops;
}

/**选择指定店铺类型 */
function selectSpecificShopType(shops: ShopDTO[], types: ShopType[]) {
    if (types.length === 0) return shops;

    const newShops: ShopDTO[] = [];

    shops.forEach((shop) => {
        if (types.includes(shop.type)) {
            newShops.push(shop);
        }
    });

    return newShops;
}

function transformGramToKg(weight) {
    return (Number(weight) * 1000) / 1000 / 1000;
}

/**
 * 电子面单排序
 * 1、主运单号排在第一个（后台排序）
 * 2、多个运单号按打印时间倒序排列
 */
const sortEwaybillNos = (order: OrderDTO, waybillNo) => {
    const rawOrder = { ...order, waybillNo };
    const ewaybillNos = rawOrder.ewaybills;

    if (ewaybillNos && ewaybillNos.length > 1) {
        const mainWaybillNo = waybillNo;

        rawOrder.ewaybills = ewaybillNos.slice().sort((e1, e2) => {
            if (e1.waybillNo === mainWaybillNo) {
                return -1;
            }
            if (e2.waybillNo === mainWaybillNo) {
                return 1;
            }
            const dateCreated1 = new Date(e1.dateCreated);
            const dateCreated2 = new Date(e2.dateCreated);
            return dateCreated2.getTime() - dateCreated1.getTime();
        });
    }

    console.log(rawOrder);

    return rawOrder;
};

const omit = <T extends {}>(obj, fields): T => {
    const shallowCopy = Object.assign({}, obj);

    for (let i = 0; i < fields.length; i += 1) {
        const key = fields[i];
        delete shallowCopy[key];
    }

    return shallowCopy;
};

const download = ({ href, downloadName = "发货易" }, e: React.MouseEvent) => {
    e.preventDefault();

    const content = new Blob([`[InternetShortcut]\nURL=${href}`]);

    FileSaver.saveAs(content, `${downloadName}.url`);
};

interface ConcurrentQueueParams<T> {
    data: T[];

    maxTask?: number;

    callback?(data: T): Promise<void>;
}

interface ConcurrentQueueResultPromise extends Promise<void> {
    cancel(): void;
}

export function concurrentQueue<T>({
    // 任务所需数据数组
    data = [],
    // 并发任务数
    maxTask = 5,
    // 自定义执行任务函数 必须是异步函数
    callback = async (data: T) => {},
}: ConcurrentQueueParams<T>): ConcurrentQueueResultPromise {
    let isQuit = false;

    const promise = new Promise<void>((yes, no) => {
        const length = data.length;

        if (length === 0) return yes();

        let index = 0;
        let active = 0;

        async function _exec() {
            if (isQuit) {
                return no("取消操作");
            }

            if (index < length && active < maxTask) {
                active++;

                callback(data[index++])
                    .then(() => {
                        active--;

                        if (active === 0 && index === data.length) {
                            yes();
                        } else {
                            _exec();
                        }
                    })
                    .catch((e) => {
                        no(new Error(e));
                    });

                _exec();
            }
        }
        // 执行
        _exec();
    });

    // @ts-ignore
    promise.cancel = () => {
        isQuit = true;
    };

    return promise as ConcurrentQueueResultPromise;
}

/** 快运快递公司 */
export function isCarrierLogisticsCompany(logisticsCompany: LogisticsCompany) {
    return [
        LogisticsCompany.BESTQJT,
        LogisticsCompany.DBL,
        LogisticsCompany.SFKY,
        LogisticsCompany.HOAU,
        LogisticsCompany.ZTO56,
        LogisticsCompany.YUNDA56,
        LogisticsCompany.ANE56,
        LogisticsCompany.STO56,
        LogisticsCompany.SUT56,
        LogisticsCompany.SXJD,
        LogisticsCompany.YMDD,
    ].includes(logisticsCompany);
}

/** 菜鸟快运模板 */
export function isCainiaoCarrierTemplate(template: WaybillTemplateDTO) {
    if (!template) return false;

    return (
        (template.type === WaybillTemplateType.CAINIAO || template.type === WaybillTemplateType.TAOBAO_CAINIAO) &&
        isCarrierLogisticsCompany(template.logisticsCompany)
    );
}

/**
 * 防止excel注入攻击
 */
export function fixExcelInjection(str: string | number | undefined) {
    const strType = typeof str;
    if (!(strType === "string" || strType === "number")) {
        return str;
    }

    let newStr = str.toString();

    if (/^[\=\+\-\@\t\r]/.test(newStr)) {
        return `'${newStr}`;
    }

    return str;
}

/**
 * 提示用户当http请求发生错误时，是否重试
 */
export async function confirmRetryHttpWhenError<T extends Function>(
    fn: T,
    props?: Omit<ModalFuncProps, "onOk"> & {
        successfulTipText?: string;
        retryErrorText?: string;
    },
) {
    try {
        await fn();
    } catch (error) {
        console.error(error);

        await new Promise<void>((yes, no) => {
            Modal.confirm({
                title: "发生错误",
                okText: "重试",
                content: "是否重试？",
                ...props,
                async onOk() {
                    try {
                        await fn();

                        if (props?.successfulTipText) {
                            message.success(props.successfulTipText);
                        }

                        yes();
                    } catch (e) {
                        no(error);

                        if (props?.retryErrorText) {
                            message.error(props.retryErrorText);
                        }
                    }
                },
                onCancel() {
                    no(error);
                },
            });
        });
    }
}

/**
 * 生成发货易的外部跳转链接
 * 传入形如 /path/to 即可
 */
export async function generateFHYRedirectUrl(path: string, withSearchParams?: (search: URLSearchParams) => void) {
    const search = new URLSearchParams();

    if (typeof withSearchParams === "function") {
        withSearchParams(search);
    }

    if (import.meta.env.VITE_IS_QN) {
        const token = await getSETokenFromQn();

        search.set("_token_", token);
    }

    return `${FD_BASE_URL}${path}?${search.toString()}`;
}

/**
 * 增加此方法目的是防止浏览器拦截window.open跳转
 */
export function openUrl(href: string, target: string = "_blank") {
    const a = document.createElement("a");

    a.href = href;

    if (target) {
        a.target = target;
    }

    a.click();

    a.remove();
}

export function isEmpty(val) {
    return val === null || val === undefined;
}

export function getOrderItemColorClass(orderItem: OrderItemDTO) {
    if (orderItem.isRefunding) {
        return "warn";
    }

    const status = orderItem.status;

    if (status === OrderStatus.PENDING_SHIPMENT || status === OrderStatus.PENDING_PAYMENT) {
        return null;
    }

    if (status === OrderStatus.SHIPPED || status === OrderStatus.SUCCESS) {
        return "text-success font-bold";
    }

    if (status === OrderStatus.CANCELED || orderItem.isRefunding) {
        return "text-danger font-bold";
    }

    return null;
}

export function getOrderColorClass(order: OrderDTO) {
    const status = order.status;

    if (status === "PENDING_SHIPMENT" || status === "PENDING_PAYMENT") {
        return null;
    }
    if (status === "SHIPPED" || status === "SUCCESS") {
        return "text-success font-bold";
    }
    if (status === "CANCELED" || status === "REFUNDING" || order.isRefunding) {
        return "text-danger font-bold";
    }
}

export function notEmpty(val) {
    return val !== null && val !== undefined;
}

export {
    uuid,
    getFormLayout,
    filterPrinters,
    isVirtualPrinter,
    numberTransformer,
    confirm,
    isNonnegativeValidator,
    timeFormat,
    isSameArrayValue,
    transformGramToKg,
    sortEwaybillNos,
    omit,
    download,
    filterSpecificShopType,
    selectSpecificShopType,
};
